<template>
  <div
    ref="container"
    class="tooltip-container"
    :class="{
      'no-border': noBorder,
    }"
    @mouseenter="showToolTip"
    @mouseleave="hideToolTip"
  >
    <slot />
    <Transition name="tooltip-fade">
      <div
        v-if="show"
        :id="`tooltip-${tooltipId}`"
        ref="tooltip"
        :aria-describedby="`tooltip-${tooltipId}`"
        class="tooltip"
        :class="{
          'tooltip-bottom-right': notch === 'bottom-right',
          'tooltip-bottom-left': notch === 'bottom-left',
          'tooltip-top-left': notch === 'top-left',
          'tooltip-top-right': notch === 'top-right',
          'tooltip-top-center': notch === 'top-center',
          'tooltip-right': notch === 'right',
        }"
        role="tooltip"
        :style="tooltipStyle"
      >
        {{ message }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';

const {
  message = '',
  notch = 'top-left',
  noBorder = false,
} = defineProps<{
  message?: string;
  notch?: 'bottom-right' | 'bottom-left' | 'top-left' | 'top-right' | 'top-center' | 'right';
  noBorder?: boolean;
}>();

const container = ref<HTMLElement | null>(null);
const tooltip = ref<HTMLElement | null>(null);
const show = ref(false);
const tooltipStyle = ref<Record<string, string>>({});
const tooltipId = `tooltip-${Math.random().toString(36).slice(2, 11)}`;

const showToolTip = async () => {
  show.value = true;
  await nextTick();
  calculatePosition();
};

const hideToolTip = () => {
  show.value = false;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const calculatePosition = () => {
  if (!container.value || !tooltip.value) return;

  const containerRect = container.value.getBoundingClientRect();
  const tooltipRect = tooltip.value.getBoundingClientRect();

  const gap = 0;

  const positionConfig = {
    'bottom-right': {
      top: containerRect.bottom + gap,
      left: containerRect.right - tooltipRect.width,
    },
    'bottom-left': {
      top: containerRect.bottom + gap,
      left: containerRect.left,
    },
    'top-right': {
      top: containerRect.top - tooltipRect.height - gap,
      left: containerRect.right - tooltipRect.width,
    },
    'top-left': {
      top: containerRect.top - tooltipRect.height - gap,
      left: containerRect.left,
    },
    'top-center': {
      top: containerRect.top - tooltipRect.height - gap,
      left: containerRect.left - tooltipRect.width / 2,
    },
    right: {
      top: containerRect.top + containerRect.height / 2 - tooltipRect.height / 2,
      left: containerRect.right + gap,
    },
  };

  const position = positionConfig[notch];
  const { clientWidth, clientHeight } = document.documentElement;
  // A capped-width message wraps tall enough to cross a viewport edge, so keep it on screen.
  // A clamped tooltip's notch no longer points at its trigger, which beats a clipped message.
  tooltipStyle.value = {
    top: `${clamp(position.top, 0, clientHeight - tooltipRect.height)}px`,
    left: `${clamp(position.left, 0, clientWidth - tooltipRect.width)}px`,
  };
};
</script>

<style>
.tooltip-container {
  display: inline-block;
  vertical-align: middle;
  width: fit-content;
  border-bottom: 2px dashed var(--color-neutral-300);
  position: relative;

  &.no-border {
    border-bottom: none;
  }

  .tooltip {
    --triangle-side: 0.5rem;
    --triangle-hypotenuse: hypot(var(--triangle-side), var(--triangle-side));

    position: fixed;
    font-size: 0.75rem;
    line-height: var(--size-tooltip-line-height);
    background-color: var(--color-neutral-700);
    color: var(--color-light);
    text-align: center;
    z-index: 10;
    width: max-content;
    max-width: var(--size-tooltip-max-width);
    overflow-wrap: anywhere;
    padding: var(--gap-xs);
    pointer-events: none;

    &:is(.tooltip-bottom-right, .tooltip-bottom-left) {
      padding-top: calc(var(--gap-xs) + var(--triangle-hypotenuse));
    }

    &:is(.tooltip-top-right, .tooltip-top-left, .tooltip-top-center) {
      padding-bottom: calc(var(--gap-xs) + var(--triangle-hypotenuse));
    }

    &.tooltip-right {
      padding-left: calc(var(--gap-xs) + var(--triangle-hypotenuse));
    }

    &.tooltip-bottom-right {
      clip-path: polygon(
        0% var(--triangle-side),
        calc(100% + -1 * (var(--triangle-side) + var(--triangle-hypotenuse))) var(--triangle-side),
        calc(100% + -1 * (var(--triangle-side) + var(--triangle-hypotenuse) / 2)) 0,
        calc(100% + -1 * (var(--triangle-side))) var(--triangle-side),
        100% var(--triangle-side),
        100% 100%,
        0% 100%
      );
    }

    &.tooltip-bottom-left {
      clip-path: polygon(
        0% var(--triangle-side),
        var(--triangle-side) var(--triangle-side),
        calc(var(--triangle-side) + var(--triangle-hypotenuse) / 2) 0%,
        calc(var(--triangle-side) + var(--triangle-hypotenuse)) var(--triangle-side),
        100% var(--triangle-side),
        100% 100%,
        0 100%,
        0% calc(100% - var(--triangle-side))
      );
    }

    &.tooltip-top-left {
      clip-path: polygon(
        0% 0%,
        100% 0%,
        100% calc(100% - var(--triangle-side)),
        1.5rem calc(100% - var(--triangle-side)),
        1rem 100%,
        var(--triangle-side) calc(100% - var(--triangle-side)),
        0% calc(100% - var(--triangle-side))
      );
    }

    &.tooltip-top-right {
      clip-path: polygon(
        0% 0%,
        100% 0%,
        100% calc(100% - var(--triangle-side)),
        calc(100% - var(--triangle-side)) calc(100% - var(--triangle-side)),
        calc(100% - 1rem) 100%,
        calc(100% - 1.5rem) calc(100% - var(--triangle-side)),
        0% calc(100% - var(--triangle-side))
      );
    }

    &.tooltip-top-center {
      clip-path: polygon(
        0% 0%,
        100% 0%,
        100% calc(100% - var(--triangle-side)),
        calc(50% + var(--triangle-side)) calc(100% - var(--triangle-side)),
        50% 100%,
        calc(50% - var(--triangle-side)) calc(100% - var(--triangle-side)),
        0% calc(100% - var(--triangle-side))
      );
    }

    &.tooltip-right {
      clip-path: polygon(
        var(--triangle-side) 0%,
        100% 0%,
        100% 100%,
        var(--triangle-side) 100%,
        var(--triangle-side) calc(50% + 0.5rem),
        0% 50%,
        var(--triangle-side) calc(50% - 0.5rem)
      );
    }
  }
}

.tooltip-fade-enter-active,
.tooltip-fade-leave-active {
  transition: var(--transition-default);
}

.tooltip-fade-enter-from,
.tooltip-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}
</style>
